import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  AuditLog,
  FilterByQuery,
  ImportBatch,
  ImportRecord,
  PaginationQuery,
  Response,
  SortByQuery,
} from "~/types/api";

export function createImportExportApi({
  httpClient,
  getRequest,
  postRequest,
  patchRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches a paginated list of import batches, optionally filtered by organization. */
  const importBatches = (organizationId: string | undefined, pagination: PaginationQuery, sortBy?: SortByQuery) =>
    getPaginationRequest(
      `/import-batches`,
      pagination,
      sortBy,
      undefined,
      organizationId ? [{ column: "organization_id", value: organizationId }] : undefined,
    );

  /** Fetches a single import batch by ID. */
  const importBatch = (batchId: string) => getRequest<Response<ImportBatch>>(`/import-batches/${batchId}`);

  /** Fetches the processing report for an import batch. */
  const importBatchReport = (batchId: string) => getRequest(`/import-batches/${batchId}/report`);

  /** Fetches a paginated list of records within an import batch. */
  const importBatchRecords = (
    batchId: string,
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    filterBy?: FilterByQuery,
  ) =>
    getPaginationRequest<ImportRecord>(`/import-batches/${batchId}/records`, pagination, sortBy, undefined, filterBy);

  /** Downloads failed import rows as a CSV file. */
  const exportFailedRows = async (batchId: string) => {
    try {
      const response = await getRequest<BlobPart>(`/import-batches/${batchId}/export-failed`, {
        responseType: "blob",
      });

      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `import-batch-${batchId}-failed-rows.csv`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading failed rows CSV:", error);
      throw error;
    }
  };

  /** Updates the row data of an import record. */
  const updateImportRecordRowData = (recordId: string, rowData: Record<string, unknown>) =>
    patchRequest<ImportRecord>(`/import-records/${recordId}`, { row_data: rowData });

  /** Retries processing a failed import record, optionally with updated row data. */
  const retryImportRecord = (recordId: string, rowData?: Record<string, unknown>) =>
    postRequest<{ success: boolean; message: string; record: ImportRecord }>(
      `/import-records/${recordId}/retry`,
      rowData ? { row_data: rowData } : {},
    );

  /** Retries all failed records in an import batch. */
  const retryBatchFailedRecords = (batchId: string) =>
    postRequest<{
      success: boolean;
      message: string;
      batch_id: string;
      failed_count: number;
      csv_exists: boolean;
      queue: string;
    }>(`/import-batches/${batchId}/retry`, {});

  /** Looks up the BSN (citizen service number) for an import record. */
  const lookupImportRecordBsn = (recordId: string) =>
    postRequest<{
      success: boolean;
      message: string;
      patients?: Array<{
        bsn: string;
        name: string;
        birth_date: string;
        gender: string;
      }>;
      auto_updated?: boolean;
      missing_fields?: {
        birth_date: boolean;
        gender: boolean;
        postal_code: boolean;
      };
      errors?: Record<string, string | string[]>;
    }>(`/import-records/${recordId}/lookup-bsn`, {});

  /** Performs a batch BSN lookup for all eligible records in an import batch. */
  const batchLookupBsn = (batchId: string) =>
    postRequest<{
      success: boolean;
      message: string;
      batch_id: string;
      eligible_records_count: number;
    }>(`/import-batches/${batchId}/batch-lookup-bsn`, {});

  /** Fetches failed queue jobs for an import batch. */
  const getQueueFailedJobs = (batchId: string) =>
    getRequest<{
      data: Array<{
        uuid: string;
        queue: string;
        exception_preview: string;
        failed_at: string;
      }>;
      count: number;
    }>(`/import-batches/${batchId}/queue-failed-jobs`);

  /** Retries failed queue jobs for an import batch. */
  const retryQueueFailedJobs = (batchId: string) =>
    postRequest<{
      success: boolean;
      message: string;
      retried_count: number;
    }>(`/import-batches/${batchId}/retry-queue-failed-jobs`, {});

  /** Fetches a paginated list of audit logs. */
  const fetchAuditLogs = (pagination: PaginationQuery, filterBy?: FilterByQuery) =>
    getPaginationRequest<AuditLog>("/audit-logs", pagination, undefined, undefined, filterBy);

  /** Fetches a paginated list of audit logs for a specific patient. */
  const fetchPatientAuditLogs = (patientId: number, pagination: PaginationQuery, filterBy?: FilterByQuery) =>
    getPaginationRequest<AuditLog>(`/patients/${patientId}/activity-logs`, pagination, undefined, undefined, filterBy);

  /** Fetches a single audit log entry by ID. */
  const fetchAuditLog = (id: string) => getRequest<Response<AuditLog>>(`/audit-logs/${id}`);

  /** Exports audit logs as a downloadable file, with optional filters applied. */
  const exportAuditLogs = (filterBy?: FilterByQuery) => {
    const params: Record<string, string | string[]> = {};
    if (filterBy) {
      filterBy.forEach((filter) => {
        if (filter.value && typeof filter.value === "object" && "from" in filter.value && "to" in filter.value) {
          const dateRange = filter.value as { from: string | null; to: string | null };
          if (dateRange.from) params["filter[date_from]"] = dateRange.from;
          if (dateRange.to) params["filter[date_to]"] = dateRange.to;
        } else if (Array.isArray(filter.value)) {
          if (filter.value.length === 0) {
            params[`filter[${filter.column}]`] = "";
          } else {
            params[`filter[${filter.column}][]`] = filter.value;
          }
        } else if (typeof filter.value !== "object") {
          params[`filter[${filter.column}]`] = String(filter.value);
        }
      });
    }
    return httpClient.get("/audit-logs/export", { params, responseType: "blob" });
  };

  return {
    importBatches,
    importBatch,
    importBatchReport,
    importBatchRecords,
    exportFailedRows,
    updateImportRecordRowData,
    retryImportRecord,
    retryBatchFailedRecords,
    lookupImportRecordBsn,
    batchLookupBsn,
    getQueueFailedJobs,
    retryQueueFailedJobs,
    fetchAuditLogs,
    fetchPatientAuditLogs,
    fetchAuditLog,
    exportAuditLogs,
  };
}

export type ImportExportApi = ReturnType<typeof createImportExportApi>;
